/**
 * AMPD DCMS2 — session guard: one sign-in over the whole page
 *
 * When the session ends while a page is open (idle or absolute limit, sign-out in
 * another tab, revoked session), the top window shows the sign-in form ONCE, in an
 * overlay over the whole page — never inside a window (iziModal frame), a drawer or a
 * menu. After the sign-in the overlay disappears and every window learns that the
 * session is valid again:
 *
 *   - open windows keep working WITHOUT a reload — an unsaved form stays as it was,
 *   - the CSRF token (derived from the session id, which the sign-in changes) is
 *     replaced in every same-origin document: <meta name="dcms-csrf-token"> and the
 *     hidden `dcms_csrf_token` fields,
 *   - only the frames that showed "sign in" instead of their content
 *     (Login::deferSignInToTopWindow()) reload,
 *   - every document gets the `dcms:session-restored` event (detail: { csrfToken }),
 *   - other tabs of this origin get the same through BroadcastChannel `dcms2-session`.
 *
 * Who shows the overlay:
 *   - the heartbeat (sessionRefresh.js) — `not-logged` after this page has seen `logged`,
 *     confirmed by `sessionSignIn.php?state=1` (the session may still be taken over
 *     from DCMS v1),
 *   - a frame of an ended session — `window.top.DCMSSessionGuard.requireSignIn()`.
 *
 * The overlay frames `/DCMS2/sessionSignIn.php?dcms_signin_here=1` — the regular sign-in
 * form, second-factor challenge included. When signing in cannot work inside a frame
 * (an identity provider that refuses frames), "Sign in in a new tab" opens the same
 * page in a tab of its own, which reports back to this window. External sign-in links
 * of that form (`[login.external]`, e.g. Keycloak, marked `data-dcms-external-sign-in`
 * by Login/login.tpl) always open such a tab: identity providers send
 * `X-Frame-Options` / `frame-ancestors`, so inside the frame they would show nothing.
 *
 * A form sent long after the last confirmed heartbeat waits for the session check
 * (holdSubmit(), installed in every window by installSubmitCheck()) — typically the
 * first click after the computer wakes up, before the 30 s heartbeat has run again.
 * A live session sends it on at once; an ended one gets the overlay, and the form stays
 * filled in instead of being posted into the sign-in page.
 *
 * Runs in the top window only; inside a frame the module does nothing.
 *
 * @copyright AMPD Sp. z o.o. / PTWP-ONLINE Sp. z o.o.
 * @link      https://www.ampd.pl
 * @see       https://docs.ampd.pl Online documentation and examples
 */

export const SESSION_SIGN_IN_URL = '/DCMS2/sessionSignIn.php';
export const SESSION_CHANNEL = 'dcms2-session';
export const SESSION_RESTORED_EVENT = 'dcms:session-restored';
export const SESSION_EXPIRED_EVENT = 'dcms:session-expired';

const SIGN_IN_HERE_PARAM = 'dcms_signin_here';
const CSRF_META = 'dcms-csrf-token';
const CSRF_FIELD = 'dcms_csrf_token';
const OVERLAY_ID = 'dcms-session-guard';
/** External sign-in link of the sign-in form (Login/login.tpl, embedded mode). */
const EXTERNAL_SIGN_IN_ATTR = 'data-dcms-external-sign-in';

/**
 * A heartbeat confirmed this long ago still vouches for the session — a form sent
 * within it goes out at once. The heartbeat runs every 30 s; a hidden tab may be
 * throttled to one run a minute, so an older confirmation is not proof any more.
 */
const SUBMIT_FRESH_MS = 45000;

/** The session check gives up after this long and lets a held form go. */
const STATE_TIMEOUT_MS = 5000;

/** Submit targets that do not open a new window (sent later, they need no user activation). */
const SAME_WINDOW_TARGETS = ['', '_self', '_parent', '_top'];

/** Texts from the commonjs dictionary (assets/lang/{LANG}.commonjs.js), English fallback. */
function t(key, fallback) {
    const table = window.i18n_commonjs;
    return (table && table[key]) ? table[key] : fallback;
}

/**
 * Same-origin documents of a window and all its frames (depth first).
 *
 * @param {Window} win
 * @param {Window[]} [out]
 * @returns {Window[]}
 */
function sameOriginWindows(win, out = []) {
    try {
        // Throws for a frame from another origin — skipped with its subtree.
        if (!win.document) {
            return out;
        }
    } catch (e) {
        return out;
    }
    out.push(win);
    for (let i = 0; i < win.frames.length; i++) {
        sameOriginWindows(win.frames[i], out);
    }
    return out;
}

class SessionGuard {
    constructor(win) {
        this.win = win;
        this.doc = win.document;
        /** True once the heartbeat has reported a signed-in session for this page. */
        this.armed = false;
        /** When the server last confirmed the session (Date.now()), 0 = never. */
        this.confirmedAt = 0;
        this.overlay = null;
        this.inertElements = [];
        /** Frames that showed "sign in" instead of their content — reloaded afterwards. */
        this.waitingFrames = new Set();
        this.checking = null;
        /** Forms waiting for the session check (holdSubmit()). */
        this.heldForms = new WeakSet();
        /** The form being sent on after the check — its submit event passes. */
        this.passThrough = null;

        this.channel = null;
        if (typeof BroadcastChannel === 'function') {
            try {
                this.channel = new BroadcastChannel(SESSION_CHANNEL);
                this.channel.onmessage = (event) => {
                    const data = event.data || {};
                    if (data.type === 'restored') {
                        this.applyRestored(data.csrfToken || '', false);
                    }
                };
            } catch (e) {
                this.channel = null;
            }
        }
    }

    /**
     * Heartbeat result (sessionRefresh.js).
     *
     * @param {'logged'|'not-logged'} status
     */
    heartbeat(status) {
        if (status === 'logged') {
            this.armed = true;
            this.confirmedAt = Date.now();
            if (this.overlay) {
                // Signed in elsewhere (another tab without BroadcastChannel, a normal sign-in
                // page): fetch the new CSRF token and close the overlay.
                this.checkState();
            }
            return;
        }
        if (status === 'not-logged' && this.armed && !this.overlay) {
            this.checkState();
        }
    }

    /**
     * Asks the server whether the session is valid; shows the overlay when it is not,
     * restores the page when it is.
     *
     * @param {string} [reason] the `dcms:session-expired` reason when the overlay appears
     * @returns {Promise<boolean|null>} true — signed in, false — signed out (the overlay
     *          is shown), null — unknown (network trouble, timeout, server error)
     */
    checkState(reason = 'heartbeat') {
        if (this.checking) {
            return this.checking;
        }
        const controller = typeof AbortController === 'function' ? new AbortController() : null;
        const timer = controller ? this.win.setTimeout(() => controller.abort(), STATE_TIMEOUT_MS) : null;
        this.checking = fetch(SESSION_SIGN_IN_URL + '?state=1', {
            credentials: 'same-origin',
            cache: 'no-store',
            headers: { Accept: 'application/json' },
            signal: controller ? controller.signal : undefined,
        })
            .then((response) => (response.ok ? response.json() : null))
            .then((state) => {
                if (!state) {
                    return null;
                }
                if (state.logged) {
                    this.confirmedAt = Date.now();
                    if (this.overlay) {
                        this.restored({ csrfToken: state.csrfToken || '' });
                    }
                    return true;
                }
                this.requireSignIn({ reason });
                return false;
            })
            .catch(() => {
                // Network trouble is not a signed-out session — the next heartbeat tries again.
                return null;
            })
            .finally(() => {
                if (timer) {
                    this.win.clearTimeout(timer);
                }
                this.checking = null;
            });
        return this.checking;
    }

    /**
     * Capture listener for `submit` of every same-origin window (installSubmitCheck()).
     *
     * A form sent when the last confirmed heartbeat is older than SUBMIT_FRESH_MS — the
     * first click after the computer wakes up, before the 30 s heartbeat has run — waits
     * for `?state=1`. Signed in (or the check failed) → the form is sent again with the
     * same button through requestSubmit(), so validation, the page's own handlers and the
     * action dock's background save run as usual. Signed out → the overlay; the form
     * stays filled in and nothing is posted into the sign-in page.
     *
     * Not held: a fresh session, a page that has not seen the session yet, the overlay
     * already shown (its own sign-in form), an event already cancelled, `method="dialog"`
     * and a target opening a new window (sent later, it would be a blocked pop-up).
     *
     * @param {SubmitEvent} event
     */
    holdSubmit(event) {
        const form = event.target;
        if (!form || form.tagName !== 'FORM' || typeof form.requestSubmit !== 'function') {
            return;
        }
        if (form === this.passThrough || event.defaultPrevented || !this.armed || this.overlay) {
            return;
        }
        if (Date.now() - this.confirmedAt < SUBMIT_FRESH_MS) {
            return;
        }
        const submitter = event.submitter || null;
        if (!this.sendsInSameWindow(form, submitter)) {
            return;
        }

        event.preventDefault();
        event.stopImmediatePropagation();
        if (this.heldForms.has(form)) {
            // A second click while the check runs.
            return;
        }
        this.heldForms.add(form);
        this.checkState('submit').then((logged) => {
            this.heldForms.delete(form);
            if (logged === false || !form.isConnected) {
                return;
            }
            this.passThrough = form;
            try {
                form.requestSubmit(submitter && submitter.form === form ? submitter : undefined);
            } catch (e) {
                // The button left the form in the meantime — send without it.
                try {
                    form.requestSubmit();
                } catch (e2) {
                    // The form cannot be sent any more.
                }
            } finally {
                this.passThrough = null;
            }
        });
    }

    /**
     * @param {HTMLFormElement} form
     * @param {HTMLElement|null} submitter
     * @returns {boolean} the submission stays in an existing window and is a real request
     */
    sendsInSameWindow(form, submitter) {
        const attr = (name) => ((submitter && submitter.getAttribute('form' + name)) || form.getAttribute(name) || '')
            .trim()
            .toLowerCase();
        return attr('method') !== 'dialog' && SAME_WINDOW_TARGETS.includes(attr('target'));
    }

    /**
     * Opens a sign-in page in a tab of its own, from the top window — after the sign-in
     * Login/sessionRestored.tpl reports back through window.opener and closes the tab.
     * Called during the click, so the browser does not block it.
     *
     * @param {string} url
     * @returns {Window|null} the tab, or null when the browser blocked it
     */
    openSignInTab(url) {
        try {
            return this.win.open(url, '_blank');
        } catch (e) {
            return null;
        }
    }

    /**
     * External sign-in links in the overlay's frame open a tab (openSignInTab()) instead
     * of loading the identity provider into the frame, which it refuses. Bound on every
     * load of the frame — the form may come back after a failed attempt or a second factor.
     *
     * @param {HTMLIFrameElement} frame
     */
    bindExternalSignIn(frame) {
        let doc = null;
        try {
            doc = frame.contentDocument;
        } catch (e) {
            return;
        }
        if (!doc) {
            return;
        }
        doc.addEventListener('click', (event) => {
            const origin = event.target;
            const link = origin && typeof origin.closest === 'function'
                ? origin.closest('a[' + EXTERNAL_SIGN_IN_ATTR + ']')
                : null;
            if (!link || event.defaultPrevented || event.button !== 0
                || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
                return;
            }
            // A tab blocked by the browser (the click reached the top window without user
            // activation): the link's own target="_blank" opens it instead. That tab has no
            // opener and stays open, but the page learns of the sign-in anyway
            // (BroadcastChannel, the next heartbeat).
            if (this.openSignInTab(link.href)) {
                event.preventDefault();
            }
        });
    }

    /**
     * Shows the sign-in overlay (once — later calls only register their frame).
     *
     * @param {{source?: Window, reason?: string}} [options]
     *        source — a frame to reload after the sign-in
     * @returns {boolean}
     */
    requireSignIn(options = {}) {
        if (options.source && options.source !== this.win) {
            this.waitingFrames.add(options.source);
        }
        if (this.overlay) {
            return true;
        }
        this.showOverlay();
        this.dispatch(SESSION_EXPIRED_EVENT, { reason: options.reason || '' });
        return true;
    }

    /**
     * The session is valid again — called by Login/sessionRestored.tpl (overlay frame or a
     * tab opened from it) and by checkState().
     *
     * @param {{csrfToken?: string}} [payload]
     */
    restored(payload = {}) {
        this.applyRestored(payload.csrfToken || '', true);
    }

    applyRestored(csrfToken, broadcast) {
        this.armed = true;
        this.confirmedAt = Date.now();
        this.hideOverlay();

        const windows = sameOriginWindows(this.win);
        for (const win of windows) {
            if (csrfToken) {
                this.replaceCsrfToken(win.document, csrfToken);
            }
        }

        // Frames that showed "sign in" instead of their content: load it now (GET of the same
        // address — a POST interrupted by the expired session is not re-sent).
        for (const frame of this.waitingFrames) {
            try {
                if (frame.location) {
                    frame.location.replace(frame.location.href);
                }
            } catch (e) {
                // Frame closed in the meantime.
            }
        }
        this.waitingFrames.clear();

        this.dispatch(SESSION_RESTORED_EVENT, { csrfToken }, windows);

        if (broadcast && this.channel) {
            try {
                this.channel.postMessage({ type: 'restored', csrfToken });
            } catch (e) {
                // Channel closed.
            }
        }
    }

    replaceCsrfToken(doc, token) {
        const meta = doc.querySelector('meta[name="' + CSRF_META + '"]');
        if (meta) {
            meta.setAttribute('content', token);
        }
        doc.querySelectorAll('input[name="' + CSRF_FIELD + '"]').forEach((input) => {
            input.value = token;
        });
    }

    dispatch(type, detail, windows = sameOriginWindows(this.win)) {
        for (const win of windows) {
            try {
                win.dispatchEvent(new win.CustomEvent(type, { detail }));
            } catch (e) {
                // Window gone.
            }
        }
    }

    signInUrl(here) {
        return SESSION_SIGN_IN_URL + (here ? '?' + SIGN_IN_HERE_PARAM + '=1' : '');
    }

    showOverlay() {
        const doc = this.doc;
        const overlay = doc.createElement('div');
        overlay.id = OVERLAY_ID;
        overlay.className = 'fixed inset-0 z-[2147483000] flex items-center justify-center bg-neutral-950/60 p-2 sm:p-4';
        overlay.setAttribute('role', 'dialog');
        overlay.setAttribute('aria-modal', 'true');
        overlay.setAttribute('aria-labelledby', OVERLAY_ID + '-title');

        const panel = doc.createElement('div');
        panel.className = 'flex w-full max-w-xl flex-col overflow-hidden rounded-lg bg-white shadow-2xl dark:bg-neutral-800';

        const header = doc.createElement('div');
        header.className = 'border-b border-neutral-200 px-4 py-3 dark:border-neutral-700';
        const title = doc.createElement('h2');
        title.id = OVERLAY_ID + '-title';
        title.className = 'text-base font-semibold text-neutral-900 dark:text-neutral-50';
        title.textContent = t('sessionGuardTitle', 'Your session has expired');
        const text = doc.createElement('p');
        text.className = 'mt-1 text-sm text-neutral-600 dark:text-neutral-300';
        text.textContent = t('sessionGuardText', 'Sign in again to get back to work. Open windows and unsaved changes stay where they are.');
        header.append(title, text);

        // The form loads only on click. A form rendered long before it is used fails with
        // a stale token, and every rendered form overwrites the sign-in token cookie — with
        // overlays in several tabs only the last rendered form would sign in.
        const start = doc.createElement('div');
        start.className = 'flex justify-center px-4 py-6';
        const signIn = doc.createElement('button');
        signIn.type = 'button';
        signIn.className = 'rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-400';
        signIn.textContent = t('sessionGuardSignIn', 'Sign in');
        signIn.addEventListener('click', () => {
            const frame = doc.createElement('iframe');
            frame.className = 'block w-full flex-1 border-0 bg-white dark:bg-neutral-900';
            frame.title = t('sessionGuardFrameTitle', 'Sign in');
            frame.addEventListener('load', () => this.bindExternalSignIn(frame));
            frame.src = this.signInUrl(true);
            panel.classList.add('h-full', 'max-h-[960px]');
            start.replaceWith(frame);
            frame.focus();
        });
        start.append(signIn);

        const footer = doc.createElement('div');
        footer.className = 'flex flex-wrap items-center justify-between gap-2 border-t border-neutral-200 px-4 py-2 text-sm dark:border-neutral-700';
        const hint = doc.createElement('span');
        hint.className = 'text-neutral-500 dark:text-neutral-400';
        hint.textContent = t('sessionGuardNewTabHint', 'Signing in does not work in this window?');
        const newTab = doc.createElement('button');
        newTab.type = 'button';
        newTab.className = 'font-medium text-blue-600 hover:underline dark:text-blue-400';
        newTab.textContent = t('sessionGuardNewTab', 'Sign in in a new tab');
        // Opened by script, so the tab can close itself and report back through window.opener.
        newTab.addEventListener('click', () => {
            this.openSignInTab(this.signInUrl(false));
        });
        footer.append(hint, newTab);

        panel.append(header, start, footer);
        overlay.append(panel);

        // The page below is unreachable while signed out — also from the keyboard.
        this.inertElements = Array.from(doc.body.children).filter((el) => !el.inert);
        this.inertElements.forEach((el) => {
            el.inert = true;
        });
        doc.body.append(overlay);
        this.overlay = overlay;
        signIn.focus();
    }

    hideOverlay() {
        if (!this.overlay) {
            return;
        }
        this.overlay.remove();
        this.overlay = null;
        this.inertElements.forEach((el) => {
            el.inert = false;
        });
        this.inertElements = [];
    }
}

/**
 * Installs the guard in the top window (once). Returns it, or null inside a frame.
 *
 * @returns {SessionGuard|null}
 */
export function initSessionGuard() {
    if (window.top !== window.self) {
        return null;
    }
    if (!window.DCMSSessionGuard) {
        window.DCMSSessionGuard = new SessionGuard(window);
    }
    return window.DCMSSessionGuard;
}

/**
 * Hands every form submission of a window — the top one or a same-origin frame — to the
 * top window's guard first (SessionGuard.holdSubmit()). A capture listener on the window
 * runs before the page's own handlers (validation, editors, the action dock's background
 * save). Without a guard in the top window (a page of another application, another
 * origin) it does nothing.
 *
 * @param {Window} win
 */
export function installSubmitCheck(win) {
    if (win.dcmsSubmitCheckInstalled) {
        return;
    }
    win.dcmsSubmitCheckInstalled = true;
    win.addEventListener('submit', (event) => {
        let guard = null;
        try {
            guard = win.top.DCMSSessionGuard;
        } catch (e) {
            // Top window from another origin.
            return;
        }
        if (guard && typeof guard.holdSubmit === 'function') {
            guard.holdSubmit(event);
        }
    }, true);
}
